import { useState } from 'react';
import { theme } from '../theme';
import { pressPlanKey, erasePlanKey, planDraftValue, fmtPlanNum } from '../utils/plan-pad';

// ---------------------------------------------------------------------------
// Хук черновика
// ---------------------------------------------------------------------------

/** Хук черновика: state + обработчики поверх plan-pad.ts. */
export function usePlanDraft(initial: number): {
  draft: string;
  value: number;
  press: (key: string) => void;
  erase: () => void;
  reset: (n: number) => void;
} {
  const [draft, setDraft] = useState<string>(initial > 0 ? String(initial) : '');

  const press = (key: string) => setDraft((d) => pressPlanKey(d, key));
  const erase = () => setDraft((d) => erasePlanKey(d));
  const reset = (n: number) => setDraft(n > 0 ? String(n) : '');

  return { draft, value: planDraftValue(draft), press, erase, reset };
}

// ---------------------------------------------------------------------------
// Сетка клавиш
// ---------------------------------------------------------------------------

/** Порядок цифровых клавиш: 1–9, затем 00, 0. */
const DIGIT_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '00', '0'];

/** Сетка клавиш 3×4: 1–9, 00, 0 + «стереть». size='check' — крупнее (для Сверки, C5). */
export function PlanPadKeys(props: {
  onKey: (key: string) => void;
  onErase: () => void;
  size?: 'inline' | 'check';
}): JSX.Element {
  const { onKey, onErase, size = 'inline' } = props;
  const isCheck = size === 'check';

  const digitStyle: React.CSSProperties = {
    padding: isCheck ? '12px 0' : '10px 0',
    minHeight: 42,
    background: 'transparent',
    border: `1px solid ${theme.colors.line}`,
    borderRadius: theme.radius.sm,
    fontFamily: theme.fontDisplay,
    fontSize: isCheck ? 18 : 17,
    color: theme.colors.ink,
    cursor: 'pointer',
  };

  const eraseStyle: React.CSSProperties = {
    ...digitStyle,
    fontFamily: theme.font,
    fontSize: 13,
    color: theme.colors.sub,
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 6,
        marginTop: 12,
      }}
    >
      {DIGIT_KEYS.map((key) => (
        <button key={key} style={digitStyle} onClick={() => onKey(key)}>
          {key}
        </button>
      ))}
      <button style={eraseStyle} onClick={onErase}>
        стереть
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Полный блок PlanPad
// ---------------------------------------------------------------------------

/** Полный блок: display + клавиши + «Готово». Используется инлайн в списках. */
export function PlanPad(props: {
  initial: number;
  /** Курсив-последствие справа от суммы; получает текущее значение черновика.
   *  Примеры у вызывающих: «≈83 ₽/день», «конверт месяца», «ожидаемый доход». */
  consequence: (value: number) => string;
  onDone: (value: number) => void;
  doneLabel?: string;
}): JSX.Element {
  const { initial, consequence, onDone, doneLabel = 'Готово' } = props;
  const { value, press, erase } = usePlanDraft(initial);

  return (
    <div
      className="rise"
      style={{
        border: `1px solid ${theme.colors.ink}`,
        borderRadius: theme.radius.sm,
        padding: 14,
      }}
    >
      {/* Строка display: цифры + подпись + последствие */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span>
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontVariationSettings: "'opsz' 144, 'WONK' 0",
              fontVariantNumeric: 'tabular-nums',
              fontSize: 36,
              lineHeight: 1,
              color: theme.colors.ink,
            }}
          >
            {fmtPlanNum(value)}
          </span>
          <span
            style={{
              fontFamily: theme.font,
              fontSize: 16,
              color: theme.colors.sub,
            }}
          >
            {' '}₽/мес
          </span>
        </span>
        <span
          style={{
            fontSize: 12,
            color: theme.colors.sub,
            fontStyle: 'italic',
          }}
        >
          {consequence(value)}
        </span>
      </div>

      {/* Клавиши */}
      <PlanPadKeys onKey={press} onErase={erase} />

      {/* Кнопка «Готово» */}
      <button
        style={{
          width: '100%',
          marginTop: 8,
          padding: '11px 0',
          background: theme.colors.ink,
          color: theme.colors.paper,
          border: 'none',
          borderRadius: theme.radius.sm,
          fontFamily: theme.fontDisplay,
          fontSize: 15,
          cursor: 'pointer',
        }}
        onClick={() => onDone(value)}
      >
        {doneLabel}
      </button>
    </div>
  );
}
